import {Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal, viewChild} from '@angular/core';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {ColumnDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {CellRangeEventArgs, FlexGrid, FormatItemEventArgs} from '@mescius/wijmo.grid';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {AccountData, ClassificationTierTarget, Kpi, PortfolioPhaseEnum} from '../../../api-client-kt/src';
import {DataDictionaryService, KpiDefinition} from '../../data/data-dictionary';
import {
    ExcelCellData,
    ExcelFmtEditable,
    ExcelFmtEditableNotAllowed,
    ExcelFmtReadonly,
    ExcelFmtReadonlyDotted,
    ExcelRow,
    WjGridCustomCells
} from '../../lib/wjgrid-customcells';
import {Stdlib} from '../../lib/stdlib';
import {WjChartModule, WjFlexChart, WjFlexPie} from '@mescius/wijmo.angular2.chart';
import {FlexChart, FlexPie, HitTestInfo} from '@mescius/wijmo.chart';
import {DataType, Globalize} from '@mescius/wijmo';
import {WjChartInteractionModule} from '@mescius/wijmo.angular2.chart.interaction';
import {WjChartAnnotationModule} from '@mescius/wijmo.angular2.chart.annotation';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {IntersectionObserverDirective} from '../../directives/intersection-observer.directive';
import {
    AccountPartitionStats,
    AccountSegmentStatisticsCalculator,
    AccountStatsBucket,
    PortfolioSegmentStatistics
} from '../../data/portfolio-segment-statistics';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {MathHelper} from '../../lib/math';
import {last} from 'es-toolkit';
import {
    PfReportDataHelper,
    UwStrategyBucketKeyAccount,
    UwStrategyBucketNotRenewable,
    UwStrategyBucketTotalExNewBusiness,
    UwStrategyBucketTotalExNewBusinessAndNotRenewable,
    UwStrategyNonPfTiersShort
} from '../../data/portfolio-segment-report-data';
import {
    AtsScatterChartComponent,
    ChartDataPoint,
    ChartTieringRange
} from '../../components/ats-scatter-chart/ats-scatter-chart.component';
import {WjCharts} from '../../lib/wjcharts';
import {WjTabStatePersistDirective} from '../../directives/wjtab-state-persist.directive';
import {PercentPipe} from '@angular/common';
import {Slider} from '@fluentui/web-components';
import {AccessControlService} from '../../security/access-control.service';
import {AccountGridComponent, AccountGridFilterCondition} from '../account-grid/account-grid.component';
import {ActivatedRoute, Router} from '@angular/router';
import {SegmentDataObserverDirective} from '../../directives/segment-data-observer.directive';


const PilSummaryColumns: ColumnDeclaration []= [
    {header: 'Bucket', binding: 'bucket', wordWrap: true, width: 120},
    {header: 'Number of Accounts', binding: 'numberAccounts', format: 'n0', width: 60},
    {header: '% of Accounts', binding: 'percentAccounts', format: 'p0', width: 60},
    {header: 'GNWP [mio EUR]', binding: 'gnwp', format: 'n1,,', width: 60},
    {header: 'GNWP %', binding: 'percentGnwp', format: 'p0', width: 60},
];

interface PilSummaryData {
    bucket: string;
    numberAccounts: number;
    percentAccounts: number;
    gnwp: number;
    percentGnwp: number;
}

interface TieringRangeData {
    from: number;
    to?: number;
    tier: string;
    fromCellFmt: ExcelCellData;
}

interface UwStrategyData {
    bucket: string;
    nlr: number | undefined;
    targetRetentionRate?: number;
    targetRetentionRateCellFmt?: ExcelCellData;
    indicativeTargetRateChange?: number;
    indicativeTargetRateChangeCellFmt?: ExcelCellData;
    targetRateChange?: number;
    targetRateChangeCellFmt?: ExcelCellData;
    finalTargetRateChange: number | undefined;
    minTargetRateChange?: number;
    minTargetRateChangeCellFmt?: ExcelCellData;
    maxTargetRateChange?: number;
    maxTargetRateChangeCellFmt?: ExcelCellData;
    targetRateChangeAdjustment?: number;
    targetRateChangeAdjustmentCellFmt?: ExcelCellData;
    newNlr?: number;
}

type KpiChartContinuousData = AccountStatsBucket & { name: string };
type KpiChartDiscreteData = (AccountPartitionStats & { key: string });
type KpiChartData = KpiChartContinuousData | KpiChartDiscreteData;

type KpiChartType = 'Continuous' | 'Discrete';
interface KpiChart { kpi: KpiDefinition; type: KpiChartType; data: KpiChartData[], numBuckets?: number, outlierQuantile?: number }

/**
 * The dashboard targets component displays the main dashboard targets page.
 * Original worksheet in Excel: 'Dashboard Targets'
 *
 * It contains the following sections:
 * - General information about the portfolio segment
 * - Tiering KPIs summary
 * - Tiering ranges
 * - UW strategy
 * - Pil calculation summary
 * - Pil calculation details
 * - Pil calculation comparison
 */
@Component({
    selector: 'app-dashboard-targets-main',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        WjGridModule,
        WjChartModule, WjChartAnnotationModule, WjChartInteractionModule, WjNavModule, IntersectionObserverDirective, AtsScatterChartComponent, WjTabStatePersistDirective, PercentPipe, SegmentDataObserverDirective
    ],
    templateUrl: './dashboard-targets-main.component.html',
    styleUrl: './dashboard-targets-main.component.scss'
})
export class DashboardTargetsMainComponent {

    private tieringRangeData: TieringRangeData[] = [];
    private uwStrategyData = signal<UwStrategyData[]>([]);

    protected readonly isDirty = signal(false);

    protected readonly Wijmo = Wijmo;
    protected readonly DataDictionaryService = DataDictionaryService;
    protected readonly GeneraliPieChartPalette = WjCharts.GeneraliPieChartPalette;
    protected readonly GeneraliColumnChartPaletteItemFormatter = WjCharts.GeneraliColumnChartPaletteItemFormatter;

    protected readonly pfData = inject(PortfolioDataContext);
    protected readonly pfSegData = inject(PortfolioSegmentDataContext);
    private readonly wb = this.pfSegData.wb;

    protected accountSegStats!: PortfolioSegmentStatistics;

    private readonly gridUwStrategy = viewChild.required<WjFlexGrid>('gridUwStrategy');
    private readonly gridCalculatedPil = viewChild.required<WjFlexGrid>('gridCalculatedPil');
    private readonly gridFinalPil = viewChild.required<WjFlexGrid>('gridFinalPil');
    private readonly gridLastYearPil = viewChild.required<WjFlexGrid>('gridLastYearPil');

    protected readonly atsChartData = computed(() => this.pfSegData.getAccountDataInFilterPeriodAndTouchFormulaEval() as ChartDataPoint[]);
    protected readonly atsChartRanges = computed(() => (this.pfSegData.classificationTierSchema.value()?.tierTargets ?? []) as ChartTieringRange[])

    protected readonly kpiCharts = signal<KpiChart[]>([]);

    private readonly accessControlSvc = inject(AccessControlService);

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    constructor() {
        effect(async () => await this.prepareReportData());
        effect(() => this.prepareAccountKpiChartData());
    }

    private editableIfGranted() {
        const isTracking = this.pfSegData.portfolio.value()?.phase === PortfolioPhaseEnum.Tracking;
        const canEditSegment = this.accessControlSvc.canEditSegmentCtx(this.pfSegData).allowed;
        return canEditSegment ? ExcelFmtEditable : ExcelFmtEditableNotAllowed;
    }

    private async pfClassificationTiers(): Promise<string[]> {
        await this.pfSegData.waitForResourcesToLoad();
        return this.pfSegData.classificationTierSchema.value()!.tiersNamesHighestFirst().slice();
    }

    async prepareAccountKpiChartData() {
        const accounts = this.pfSegData.getAccountDataInFilterPeriod();
        this.accountSegStats = this.wb.calculateStatistics();

        const kpis = this.pfSegData.segmentKpis.value();
        const kpisDefs = kpis.map(kpi => DataDictionaryService.getKpiDefinition(kpi.attribute)!);

        const kpiCharts: KpiChart[] = kpisDefs.map(kpi => {
            const attribute = kpi.attribute as keyof AccountData;
            const storedHistogramSettings = this.getStoredHistogramSettings(attribute);
            const numBuckets = storedHistogramSettings?.numBuckets ?? 10;
            const outlierQuantile = storedHistogramSettings?.outlierQuantile ?? 0.01;

            let data: KpiChartData[];
            if (kpi.isDiscrete) {
                const statsAsObject = AccountSegmentStatisticsCalculator.aggregatePartition(accounts, a => a[attribute] as PropertyKey);
                data = Object.keys(statsAsObject).map(key => ({ key: key.toString(), ...statsAsObject[key] }));
            } else {
                data = this.generateHistogram(accounts, attribute, kpi, numBuckets, outlierQuantile);
            }
            return {
                kpi, type: kpi.isDiscrete ? 'Discrete' : 'Continuous', data, outlierQuantile, numBuckets
            }
        });

        this.kpiCharts.set(kpiCharts);
    }

    private generateHistogram(accounts: AccountData[], attribute: string, kpi: KpiDefinition, numBuckets: number, outlierQuantile: number): KpiChartData[] {
        const accountValueGetter = (a: AccountData): number => a[attribute as keyof AccountData] as number ?? 0;
        const histogram = AccountSegmentStatisticsCalculator.aggregateAsHistogram(accounts, numBuckets, outlierQuantile, accountValueGetter);

        const format = kpi.format ?? '';
        return histogram.map(entry => ({
            name: `${Globalize.formatNumber(entry.min, format)} - ${Globalize.formatNumber(entry.max, format)}`,
            ...entry
        }));
    }

    protected onOutlierQuantileChange(chart1: FlexChart, chart2: FlexChart, kpiChart: KpiChart, attribute: 'numBuckets' | 'outlierQuantile', control: HTMLElement | null) {
        if (control != null)  {
            const isInitalized = control.dataset["initialized"] === 'true';
            if (!isInitalized) {
                control.dataset["initialized"] = 'true';
                return;
            }
        }

        const slider = control as Slider
        const value = parseFloat(slider.value);
        if (!Number.isFinite(value)) return;

        const numBuckets = (attribute === 'numBuckets') ? value : kpiChart.numBuckets;
        const outlierQuantile = (attribute === 'outlierQuantile') ? value : kpiChart.outlierQuantile;

        if (numBuckets !== kpiChart.numBuckets || outlierQuantile !== kpiChart.outlierQuantile) {
            kpiChart.outlierQuantile = outlierQuantile;
            kpiChart.numBuckets = numBuckets;
            this.saveHistogramSettings(kpiChart.kpi.attribute, numBuckets!, outlierQuantile!);

            const accounts = this.pfSegData.getAccountDataInFilterPeriod();
            kpiChart.data = this.generateHistogram(accounts, kpiChart.kpi.attribute as keyof AccountData, kpiChart.kpi, numBuckets!, outlierQuantile!);
            chart1.refresh(true);
            chart2.refresh(true);
        }
    }

    private getStoredHistogramSettings(kpiAttribute: string): { numBuckets: number, outlierQuantile: number } | undefined {
        const storedSettings = sessionStorage.getItem(`histogram-settings-${kpiAttribute}`);
        if (storedSettings) {
            return JSON.parse(storedSettings);
        }
        return undefined;
    }

    private saveHistogramSettings(kpiAttribute: string, numBuckets: number, outlierQuantile: number) {
        sessionStorage.setItem(`histogram-settings-${kpiAttribute}`, JSON.stringify({ numBuckets, outlierQuantile }));
    }

    /**
     * Applies some default behaviors to the specified FlexGrid instance.
     *  - Enables custom cell formatting
     *  - Unselecting all cells upon grid focus loss
     *  - Ensures that only one grid has an active cell at a time.
     *
     * @param {FlexGrid} grid - The FlexGrid instance to which the default dashboard behavior will be applied.
     * @return {void} This method does not return a value.
     */
    static decorateGridWithDashboardDefaultBehaviour(grid: FlexGrid): void {
        WjGridCustomCells.enableCustomCellFormat(grid);
        WjGridUtils.unselectAll(grid);
        WjGridUtils.autoUnselectWhenFocusLost(grid);
        WjGridUtils.activateCopyUnformattedMode(grid);
        WjGridUtils.registerSelectedCellValueSignal(grid);

        // all numbers in a 'totals' row should appear in bold
        grid.formatItem.addHandler(this.formatTotalsRows.bind(this));
    }

    /**
     * Formats rows in a FlexGrid that represent totals by applying specific styling.
     */
    private static formatTotalsRows(s: FlexGrid, e: FormatItemEventArgs) {
        if (e.panel === s.cells) {
            const cell = e.cell;
            const item = s.collectionView.items[e.row];
            const bucket = item?.bucket;
            if (typeof (bucket) === 'string' && bucket.indexOf('Total') >= 0) {
                cell.classList.add('bold-text');
            }
        }
    }

    async onGeneralDataGridInitialized(grid: FlexGrid) {
        await this.pfSegData.waitForResourcesToLoad();

        WjGridUtils.addColumns(grid, [
            { header: 'Label', binding: 'label', cssClass: 'site-accent-label', width: 140 },
            { header: 'Value', binding: 'col1', align: 'center' },
        ]);

        type InputDataRow = { label: string, col1: number | Date | undefined, col1CellFmt: ExcelCellData};

        const uwy = this.wb.uwy;
        const plr = this.wb.configMatrixValue('plr');
        const nlr =  this.wb.configMatrixValue('nlr');
        const gnwp =  this.wb.configMatrixValue('gnwp');
        const adequacy = MathHelper.nullableDiv(plr, nlr);

        const values : InputDataRow[] = [
            { label: `${uwy} NLR*`, col1: nlr, col1CellFmt: { format: 'p1', isReadOnly: false, cssClass: 'extra' } },
            { label: `${uwy} Plan LR`, col1: plr, col1CellFmt: { format: 'p1', isReadOnly: true } },
            { label: 'Plan Loss Ratio Adequacy', col1: adequacy, col1CellFmt: { format: 'p1', isReadOnly: true } },
            { label: `${uwy} Plan GNWP, m EUR`, col1: gnwp, col1CellFmt: { format: 'n1,,', isReadOnly: true } },
            { label: 'UW Year', col1: uwy, col1CellFmt: { format: 'g0', isReadOnly: true } },
            { label: 'Tiering data as of', col1: new Date(), col1CellFmt: { format: 'dd.MM.yyyy', isReadOnly: true } },
        ];

        grid.itemsSource = values;
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
        grid.autoSizeColumn(0, false);
    }

    async onTieringKpisSummaryGridInitialized(grid: FlexGrid) {
        WjGridUtils.addColumns(grid, [
            { header: 'Tiering Criteria', binding: 'criteria', wordWrap: true, width: 120 },
            { header: 'Direction', binding: 'directionIcon', align: 'center', isContentHtml: true, width: 60 },
            { header: 'Weight', binding: 'weight', align: 'center', format: 'p0', width: 60 },
            { header: 'Actual Weight', binding: 'actualWeight', align: 'center', format: 'p0', width: 60 },
        ]);

        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);

        const kpiResponse = await Stdlib.resourceDataToPromise(this.pfSegData.segmentKpis);
        const data = kpiResponse.map(k => this.toKpiSummary(k));
        data.push( {
            criteria: 'Sum of weights',
            directionIconCellFmt: ExcelFmtReadonlyDotted,
            weight: 1,
            actualWeight: 1,
            rowFmt: {
                cssClass: 'bold-text'
            }
        });
        grid.itemsSource = data;
    }

    private toKpiSummary(kpi: Kpi): ExcelRow {
        const kpiDefinition = DataDictionaryService.getKpiDefinition(kpi.attribute);

        return {
            criteria: kpiDefinition!.name,
            directionUp: kpi.directionUp,
            directionIcon: DataDictionaryService.kpiDirectionIcon(kpi.directionUp),
            weight: kpi.weight,
            actualWeight: kpi.weight
        };
    }

    private findRangeData(name: string): TieringRangeData {
        return this.tieringRangeData.find(it => it.tier === name)!;
    }

    private findUwStrategyData(bucket: string): UwStrategyData {
        return this.uwStrategyData().find(d => d.bucket === bucket)!;
    }

    async onTieringRangesGridInitialized(grid: FlexGrid) {
        const tieringSchema = await Stdlib.resourceDataToPromise(this.pfSegData.classificationTierSchema);
        const tieringNames = tieringSchema.tierNames;

        //const divInput = document.createElement('div');
        WjGridUtils.addColumns(grid, [
            { header: 'From', binding: 'from', format: 'p1', align: 'center' },
            { header: 'To', binding: 'to', align: 'center', format: 'p1', isReadOnly: true, isRequired: false },
            { header: 'Tier', binding: 'tier', align: 'center', isReadOnly: true }
        ]);

        this.tieringRangeData = tieringSchema.tierTargets.map((r, idx) =>
            ({
                from: r.fromValue,
                fromCellFmt: (idx > 0) ? this.editableIfGranted() : ExcelFmtReadonly,
                to: r.toValue ?? undefined,
                tier: r.tierName
            })
        );

        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
        grid.itemsSource = this.tieringRangeData;
        WjGridUtils.addCellValueChangeHandler(grid, (s, e) => {
            // logic: ranges must not overlap, so if one range changes,
            // we need to check if the new range overlaps with any other range
            for (let i = 1; i < tieringNames.length; i++) {
                const thisRange = this.findRangeData(tieringNames[i]);
                const prevRange = this.findRangeData(tieringNames[i-1]);
                thisRange.from = Math.max(thisRange.from!, prevRange.from!);
                prevRange.to = thisRange.from!;
            }
            this.saveTargetData();
        });
    }

    async onUwStrategyGridInitialized(grid: FlexGrid) {
        await this.pfSegData.waitForResourcesToLoad();

        const tieringSchema = await Stdlib.resourceDataToPromise(this.pfSegData.classificationTierSchema)
        const tiersHighestFirst = await this.pfClassificationTiers();
        const uwStrategy = this.findUwStrategyData.bind(this);

        const uwy = this.wb.uwy;
        const nlr = this.wb.configMatrixValue('nlr');

        WjGridUtils.addColumns(grid, [
            { header: 'Bucket', binding: 'bucket', wordWrap: true, width: 120 },
            { header: `${uwy} NLR*`, binding: 'nlr', format: 'p1', isReadOnly: true, align: 'right', isRequired: false },
            { header: 'Target Retention Rate', binding: 'targetRetentionRate', format: 'p1', align: 'right', isRequired: false },
            { header: 'Indicative Target Rate Change', binding: 'indicativeTargetRateChange', format: 'p1', align: 'right' },
            { header: 'Target Rate Change', binding: 'targetRateChange', format: 'p1', align: 'right' },
            { header: 'Final Target Rate Change', binding: 'finalTargetRateChange', format: 'p1', align: 'right' },
            { header: 'Min Target Rate Change', binding: 'minTargetRateChange', format: 'p1', align: 'right' },
            { header: 'Max Target Rate Change', binding: 'maxTargetRateChange', format: 'p1', align: 'right' },
            { header: 'New NLR', binding: 'newNlr', format: 'p1', isReadOnly: true, align: 'right' },
            { header: 'Adjusting Factor', binding: 'targetRateChangeAdjustment', format: 'p3', dataType: DataType.Number, align: 'right', visible: false },
        ]);

        ['targetRetentionRate', 'targetRateChange', 'minTargetRateChange', 'maxTargetRateChange']
            .forEach(col => WjGridCustomCells.setColumnFormat(grid.getColumn(col), ExcelFmtReadonly));


        const uwStrategyData = tiersHighestFirst.map(r => {
                const tierData = tieringSchema.tierData(r);
                return ({
                    bucket: r,
                    nlr: undefined,
                    targetRetentionRate: tierData.targetRetentionRate,
                    indicativeTargetRateChange: tierData.targetRateChange,
                    targetRateChange: tierData.targetRateChange,
                    finalTargetRateChange: Stdlib.nvl2(tierData.targetRetentionRate, tierData.targetRateChange, undefined),
                    minTargetRateChange: tierData.targetRateChangeMin,
                    maxTargetRateChange: tierData.targetRateChangeMax,
                    newNlr: 0,
                    targetRateChangeAdjustment: tierData.targetRateChangeAdjustment,
                } as UwStrategyData);
            }
        );
        uwStrategyData.push(...[
            { bucket: UwStrategyBucketKeyAccount },
            { bucket: UwStrategyBucketTotalExNewBusiness },
            { bucket: UwStrategyBucketTotalExNewBusinessAndNotRenewable },
        ] as any[]);

        this.uwStrategyData.set(uwStrategyData);
        await this.prepareReportData();

        tieringSchema.tierNames.forEach(tier => {
            const strategy = uwStrategy(tier);
            strategy.targetRetentionRateCellFmt = this.editableIfGranted();
            strategy.targetRateChangeCellFmt = this.editableIfGranted();
            strategy.maxTargetRateChangeCellFmt = this.editableIfGranted();
        });

        const highestTierName = last(tieringSchema.tierNames)!;
        uwStrategy(highestTierName).minTargetRateChangeCellFmt = this.editableIfGranted();

        UwStrategyNonPfTiersShort.forEach(bucket => {
            const strategy = uwStrategy(bucket);
            strategy.minTargetRateChangeCellFmt = ExcelFmtReadonlyDotted;
            strategy.maxTargetRateChangeCellFmt = ExcelFmtReadonlyDotted;
        });

        const keyAccountStrategy = uwStrategy(UwStrategyBucketKeyAccount);
        keyAccountStrategy.indicativeTargetRateChangeCellFmt = ExcelFmtReadonlyDotted;
        keyAccountStrategy.targetRateChangeCellFmt = ExcelFmtReadonlyDotted;

        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
        grid.itemsSource = this.uwStrategyData();

        WjGridUtils.addCellValueChangeHandler(grid, this.onUwStrategyGridCellValueChanged.bind(this));
    }

    /**
     * Cell-Value-Change handler for the UW-Strategy grid.
     * This handler copies entered values entered in
     *    1) in the column "Target Retention Rate" to column "Final Target Rate Change" (if not empty)
     *    2) and in the column "Max Target Rate Change" to column "Min Target Rate Change" to create a gap free interval
     */
    private async onUwStrategyGridCellValueChanged(s: FlexGrid, e: CellRangeEventArgs){
        const tiersHighestFirst = await this.pfClassificationTiers();
        for (let i = 0; i < tiersHighestFirst.length; i++) {
            const thisRow = this.findUwStrategyData(tiersHighestFirst[i]);
            thisRow.finalTargetRateChange = Stdlib.nvl2(thisRow.targetRetentionRate, thisRow.targetRateChange, undefined);

            if (i > 0) { // first tier has manually entered min value
                const prevRow = this.findUwStrategyData(tiersHighestFirst[i - 1]);
                thisRow.minTargetRateChange = prevRow.maxTargetRateChange;
            }
        }
        this.saveTargetData();
    }

    async onCalculatedPilGridInitialized(grid: FlexGrid) {
        await this.pfSegData.waitForResourcesToLoad();
        WjGridUtils.addColumns(grid, PilSummaryColumns);
        grid.itemsSource = await this.generateFullPilSummary(this.accountSegStats.statsByCalculatedTier);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    private static pilSummaryFromStats(bucket: string, stats: AccountPartitionStats): PilSummaryData {
        return {
            bucket,
            numberAccounts: stats?.count ?? 0,
            percentAccounts: stats?.countPct ?? 0,
            gnwp: stats?.gnwpEur ?? 0,
            percentGnwp: stats?.gnwpEurPct ?? 0
        };
    }

    private async generateFullPilSummary(pilStats: { [key: string] : AccountPartitionStats }): Promise<PilSummaryData[]> {
        const classificationTiers = await this.pfClassificationTiers();
        const specialTiers = [UwStrategyBucketKeyAccount, UwStrategyBucketNotRenewable, AccountSegmentStatisticsCalculator.NotTieredStatus];

        const tiers = [...classificationTiers, ...specialTiers];
        return tiers.map(bucket => DashboardTargetsMainComponent.pilSummaryFromStats(bucket, pilStats[bucket]));
    }

    async onFinalPilGridInitialized(grid: FlexGrid) {
        await this.pfSegData.waitForResourcesToLoad();
        WjGridUtils.addColumns(grid, PilSummaryColumns);
        grid.itemsSource = await this.generateFullPilSummary(this.accountSegStats.statsByFinalTier);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    async onLastYearPilGridInitialized(grid: FlexGrid) {
        await this.pfSegData.waitForResourcesToLoad();
        WjGridUtils.addColumns(grid, PilSummaryColumns);
        grid.itemsSource = await this.generateFullPilSummary(this.accountSegStats.statsByLastYearTier);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    getLabelContent(ht: HitTestInfo) {
        const data = ht.item;
        const formattedValue = Globalize.formatNumber(data.gnwpEur ?? 0, 'n1,,');
        const formattedPctValue = Globalize.formatNumber(data.gnwpEurPct ?? 0, 'p1');
        return `
            <tspan dx="0" font-weight="bold">${formattedValue} mio</tspan>
            <tspan dx="-35" dy="15">(${formattedPctValue})</tspan>
        `;
    }

    getLabelContentCount(ht: HitTestInfo) {
        const data = ht.item;
        return `
            <tspan dx="0" font-weight="bold">${data.count}</tspan>
            <tspan dx="-35" dy="15">(${(data.countPct*100).toFixed(1)}%)</tspan>
        `;
    }

    private async saveTargetData() {
        const tieringSchema = await Stdlib.resourceDataToPromise(this.pfSegData.classificationTierSchema)
        const ranges = tieringSchema.tierTargets;

        const newRanges: ClassificationTierTarget[] = ranges.map(r => {
            const range = this.findRangeData(r.tierName);
            const strategy = this.findUwStrategyData(r.tierName);
            return {
                ...r,
                fromValue: range.from,
                toValue: range.to,
                targetRetentionRate: strategy.targetRetentionRate,
                targetRateChange: strategy.targetRateChange,
                targetRateChangeMin: strategy.minTargetRateChange,
                targetRateChangeMax: strategy.maxTargetRateChange,
                targetRateChangeAdjustment: strategy.targetRateChangeAdjustment
            };
        });

        this.pfSegData.updateClassificationTierSchema(newRanges);
        this.isDirty.set(true);
        this.pfSegData.updateFormulasAndValidations();
    }

    async onApply() {
        await this.saveTargetData();
    }

    async onAllocateRateChange() {
        this.pfSegData.updateFormulasAndValidations()
    }

    private async prepareReportData() {
        const summary = structuredClone(this.pfSegData.wb.uwySummary());

        this.uwStrategyData().forEach(uws => {
            const bucket = uws.bucket;
            const summaryRow = PfReportDataHelper.findSummaryRow(summary, uws.bucket);
            if (summaryRow) {
                uws.nlr = summaryRow.nlr;
                uws.newNlr = summaryRow.renewalNlr;
                uws.indicativeTargetRateChange = summaryRow.indicativeTargetRateChange;
                uws.targetRateChange = summaryRow.targetRateChange;
            }
            if (bucket === UwStrategyBucketKeyAccount) {
                uws.finalTargetRateChange = summaryRow?.targetRateChange;
                uws.targetRetentionRate = summaryRow?.targetRetentionRate;
            }  else if (/total/i.test(bucket)) {
                uws.finalTargetRateChange = summaryRow?.targetRateChange;
                uws.targetRetentionRate = summaryRow?.targetRetentionRate;
            }
        });
        this.gridUwStrategy()?.refresh(true);

        await this.pfSegData.waitForResourcesToLoad();
        this.accountSegStats = this.wb.calculateStatistics();
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridCalculatedPil(), await this.generateFullPilSummary(this.accountSegStats.statsByCalculatedTier));
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridFinalPil(), await this.generateFullPilSummary(this.accountSegStats.statsByFinalTier));
    }

    protected async onChartClick(kpiChart: KpiChart, chart: FlexChart | FlexPie, event: MouseEvent) {
        const ht = chart.hitTest(event);
        const item = ht.item;
        const binding = kpiChart.kpi.attribute;

        if (item && ht.distance === 0) {
            if (chart instanceof FlexChart) {
                const filter: AccountGridFilterCondition<string> = { column: binding, type: 'range', value1: item.min, value2: item.max };
                await AccountGridComponent.applyFilterAndShow(this.router, this.route, filter);
            } else if (chart instanceof FlexPie) {
                const filter: AccountGridFilterCondition<string> = { column: binding, type: 'equals', value1: item.key };
                await AccountGridComponent.applyFilterAndShow(this.router, this.route, filter);
            }
        }
    }

    protected onChartInitialized(chart: WjFlexPie | WjFlexChart) {
        chart.tooltip.showDelay = 500;
    }
}
